import React from 'react';
import Icon from '../../../components/AppIcon';

const MetricsCard = ({ 
  title, 
  value, 
  change, 
  changeType, 
  icon, 
  description, 
  sparklineData = [],
  className = "" 
}) => {
  const getChangeColor = () => {
    if (changeType === 'positive') return 'text-emerald-600 bg-emerald-50';
    if (changeType === 'negative') return 'text-red-600 bg-red-50';
    return 'text-slate-600 bg-slate-50';
  };

  const getChangeIcon = () => {
    if (changeType === 'positive') return 'TrendingUp';
    if (changeType === 'negative') return 'TrendingDown';
    return 'Minus';
  };

  // Simple sparkline path generation
  const generateSparklinePath = (data) => {
    if (!data || data?.length === 0) return '';
    
    const width = 60;
    const height = 20;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    
    const points = data?.map((value, index) => {
      const x = (index / (data?.length - 1)) * width;
      const y = height - ((value - min) / range) * height;
      return `${x},${y}`;
    })?.join(' ');
    
    return `M ${points?.replace(/,/g, ' L ')?.replace('L', 'M')}`;
  };

  return (
    <div className={`group relative bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 ${className}`}>
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 to-purple-50/30 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative">
        {/* Header with icon and change indicator */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl group-hover:from-indigo-200 group-hover:to-purple-200 transition-colors duration-300">
              <Icon name={icon} size={24} className="text-indigo-600" />
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-slate-600">{title}</p>
            </div>
          </div>
          
          <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold ${getChangeColor()}`}>
            <Icon name={getChangeIcon()} size={12} />
            <span>{change}</span>
          </div>
        </div>

        {/* Mobile title (shown only on mobile when hidden above) */}
        <div className="sm:hidden mb-3">
          <p className="text-sm font-medium text-slate-600">{title}</p>
        </div>

        {/* Main value and sparkline */}
        <div className="flex items-end justify-between mb-3">
          <div>
            <h3 className="text-3xl font-bold text-slate-900 mb-1">{value}</h3>
            <p className="text-sm text-slate-500">{description}</p>
          </div>
          
          {sparklineData?.length > 0 && (
            <div className="flex flex-col items-end">
              <svg width="60" height="20" className="text-indigo-500 opacity-70">
                <defs>
                  <linearGradient id={`gradient-${title?.replace(/\s+/g, '')}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="currentColor" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="currentColor" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
                <path
                  d={generateSparklinePath(sparklineData)}
                  stroke={`url(#gradient-${title?.replace(/\s+/g, '')})`}
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xs text-slate-400 mt-1">Last 30 days</span>
            </div>
          )}
        </div>

        {/* Progress indicator for certain metrics */}
        {title?.includes('Rate') && (
          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-1000 ease-out"
              style={{ 
                width: `${Math.min(parseFloat(value?.replace('%', '')) || 0, 100)}%` 
              }}
            />
          </div>
        )}

        {/* Hover overlay for additional context */}
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-slate-900/5 to-transparent rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Icon name="Info" size={12} />
            <span>Click for detailed breakdown</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsCard;